/**
 * Box-mesh placeholders: AI traffic bundles and procedural player truck fallback
 * when OBJ rigs fail over the network — preserves readable silhouettes at oblique camera angles.
 */

import * as THREE from "three";

/**
 * Arcade traffic capsule — headlights face local −Z (“into” the scrolling direction).
 *
 * @param {ReturnType<import("./materials.js").createRaceMaterials>} materials
 * @param {THREE.MeshStandardMaterial} bodyMaterial paint for hull
 * @param {boolean} isAiTruck long wheelbase variant mimicking rigs
 */
export function createAiTrafficVehicle(materials, bodyMaterial, isAiTruck) {
  const car = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(isAiTruck ? 1.55 : 1.25, isAiTruck ? 0.75 : 0.45, isAiTruck ? 3.2 : 2.2),
    bodyMaterial
  );
  body.position.y = isAiTruck ? 0.58 : 0.42;
  body.castShadow = true;
  body.receiveShadow = true;
  car.add(body);

  const cabinMat = isAiTruck ? bodyMaterial : materials.glass;
  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(isAiTruck ? 1.35 : 0.9, isAiTruck ? 0.62 : 0.44, isAiTruck ? 1.0 : 0.86),
    cabinMat
  );
  cabin.position.set(0, isAiTruck ? 1.13 : 0.82, isAiTruck ? -0.7 : -0.12);
  cabin.castShadow = true;
  car.add(cabin);

  if (!isAiTruck) {
    const hood = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.12, 0.8), bodyMaterial);
    hood.position.set(0, 0.72, -0.75);
    hood.castShadow = true;
    car.add(hood);
  }

  for (const x of [-0.72, 0.72]) {
    for (const z of [-0.72, 0.72]) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.18, 16), materials.black);
      wheel.position.set(x, 0.2, isAiTruck ? z * 1.55 : z);
      wheel.rotation.z = Math.PI / 2;
      wheel.castShadow = true;
      car.add(wheel);
    }
  }

  const lightMat = materials.white;
  for (const x of [-0.36, 0.36]) {
    const light = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.08, 0.04), lightMat);
    light.position.set(x, 0.46, -1.12);
    car.add(light);
  }

  return car;
}

/**
 * Player fallback — red cabin stack so we still read silhouette post-crash rotations.
 *
 * @param {ReturnType<import("./materials.js").createRaceMaterials>} materials
 */
export function createFallbackPlayerTruckGroup(materials) {
  const isAiTruck = true;
  const fallback = createAiTrafficVehicle(materials, materials.accent, isAiTruck);
  /* Match the exaggerated OBJ footprint so placeholders do not dwarf against loaded rigs. */
  fallback.scale.multiplyScalar(1.85);
  return fallback;
}
